/**
 * 🌅 Hero — Iter 216 (2026-06-30)
 *
 * Full-bleed editorial human-hero photograph with a left-aligned dark
 * gradient overlay carrying the headline + the two CTAs. Replaces the
 * prior split-pane "text-left / black-by-the-numbers-box-right" layout
 * that the SA called "fragmented with a lot of open white space."
 *
 * Iter 216: SA requested
 *   1. A photo with broader ethnic representation showing the FULL
 *      person, not a cropped portrait that gets blown out by object-cover.
 *   2. The three "by the numbers" metrics anchored ACROSS THE BOTTOM
 *      of the hero so they sit inside the initial viewport with zero
 *      scroll — they now ride a translucent dark strip over the photo
 *      rather than living in a standalone section below.
 */
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake } from "lucide-react";
import { CommercialCta } from "@/components/commercial/CommercialCta";
import { MetalWordmark } from "./MetalWordmark";
import WatchPlatformButton from "@/components/WatchPlatformButton";
import { ResidentWelcomeButton } from "@/components/ResidentWelcomeButton";
import { TAGLINE_LINE_1, TAGLINE_LINE_2, TAGLINE_SCRIPTURE } from "../brand";

const HERO_IMG_URL = "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=2400";

const HERO_METRICS = [
  { value: "159",       label: "Residential Beds",   testid: "hero-stat-residential-beds" },
  { value: "250+",      label: "Partner Agencies",   testid: "hero-stat-partner-agencies" },
  { value: "$45K–$55K", label: "Career Track Wages", testid: "hero-stat-career-track" },
];

export default function Hero() {
  return (
    <section
      className="relative min-h-[85vh] lg:min-h-[90vh] w-full overflow-hidden bg-[#0A0612] flex flex-col"
      data-testid="hero-section"
    >
      {/* Photograph */}
      <img
        src={HERO_IMG_URL}
        alt="A diverse, multi-ethnic team of TEA partners and program graduates collaborating in a modern workplace — the Last, the Least and the Lost, now standing in stable, dignified careers."
        className="absolute inset-0 h-full w-full object-cover object-center"
        loading="eager"
        decoding="async"
      />

      {/* Left-aligned dark overlay — 90% → 60% → 0%, plus a stronger
          full-coverage tint on mobile so the type still passes AA */}
      <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-[#0A0612]/90 via-[#0A0612]/60 to-transparent" />
      <div className="absolute inset-0 md:hidden bg-gradient-to-b from-[#0A0612]/90 via-[#0A0612]/80 to-[#0A0612]/70" />

      {/* Content slab — flex-1 lets the stats strip dock to the bottom */}
      <div className="relative z-10 flex-1 px-6 md:px-12 lg:px-24 max-w-[1400px] w-full mx-auto flex items-center">
        <div className="max-w-3xl py-20 lg:py-24">
          {/* Eyebrow */}
          <div
            className="flex w-fit mx-auto sm:mx-0 sm:inline-flex items-center gap-2.5 mb-8 px-4 py-1.5 bg-white/10 backdrop-blur-md rounded-full border border-white/20"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/90">
              Commitment to Career · Three Phases
            </span>
          </div>

          {/* 🥇 PATCH-500 · Name first, metallic lettering: gold THE · teal EXCHANGE · gold ALLIANCE. */}
          <h1
            className="font-display text-[clamp(1.25rem,7.2vw,2.25rem)] sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] whitespace-nowrap text-center sm:text-left"
            data-testid="hero-headline"
          >
            <MetalWordmark testId="hero-wordmark" />
          </h1>

          <div className="mt-5 w-fit max-w-full mx-auto sm:mx-0" data-testid="hero-tagline-block">
            <p
              className="font-display text-xl md:text-2xl lg:text-3xl font-bold tracking-tight leading-snug text-white/95"
              data-testid="hero-subline"
            >
              <span className="block text-center sm:text-left">{TAGLINE_LINE_1}:</span>
              <span className="block text-center text-[#F4D88A]" data-testid="hero-subline-mandate">{TAGLINE_LINE_2}</span>
            </p>
            <div
              className="mt-3 text-center font-mono text-[10px] tracking-[0.25em] uppercase text-white/60"
              data-testid="hero-scripture"
            >
              {TAGLINE_SCRIPTURE}
            </div>
          </div>

          {/* Mission paragraph */}
          <p
            className="mt-8 text-base md:text-lg text-white/75 leading-relaxed max-w-xl mx-auto sm:mx-0 text-center sm:text-left"
            data-testid="hero-mission"
          >
            Our Commitment to Career is how we live out that mandate: three phases —{" "}
            <span className="text-white font-medium">Triage</span>,{" "}
            <span className="text-white font-medium">Sanctuary</span>, and{" "}
            <span className="text-white font-medium">Career</span> — that we
            establish and hold the standard for, administered alongside
            credentialed partner agencies, so the people the world steps over
            are seen, housed, trained, and sent back out standing on their own
            feet.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col items-center gap-4 sm:w-fit" data-testid="hero-ctas">
          <div className="flex flex-wrap items-center justify-center gap-4" data-testid="hero-ctas-top">
            <Link
              to="/intake"
              data-testid="hero-primary-cta"
              className="
                group inline-flex items-center gap-2.5
                bg-[#1B4332] hover:bg-[#2D6A4F]
                text-white px-8 py-4 rounded-full
                font-semibold tracking-tight text-base
                transition-all duration-300
                shadow-lg shadow-[#1B4332]/30 hover:shadow-xl hover:shadow-[#2D6A4F]/40
                hover:scale-[1.02]
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B4332] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0612]
              "
            >
              <HeartHandshake className="h-5 w-5" />
              Get Assistance Now
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>

            <Link
              to="/donate"
              data-testid="hero-secondary-cta"
              className="
                group inline-flex items-center gap-2.5
                bg-transparent border border-white/30 hover:bg-white/10 hover:border-white/50
                text-white px-8 py-4 rounded-full
                font-semibold tracking-tight text-base
                backdrop-blur-sm
                transition-all duration-300
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0612]
              "
            >
              Partner With Us · Donate
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4" data-testid="hero-ctas-bottom">
            <CommercialCta source="hero" className="group inline-flex items-center gap-2.5 bg-[#D4AF37] hover:bg-[#F4D88A] text-[#0A0612] px-8 py-4 rounded-full font-semibold tracking-tight text-base transition-all duration-300 shadow-lg shadow-[#D4AF37]/30 hover:shadow-xl hover:shadow-[#F4D88A]/40 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
            <WatchPlatformButton className="group inline-flex items-center gap-2.5 bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-4 rounded-full font-semibold tracking-tight text-base backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
            <ResidentWelcomeButton testid="hero-resident-welcome" className="group inline-flex items-center gap-2.5 bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-4 rounded-full font-semibold tracking-tight text-base backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
          </div>
          </div>

          {/* Mini tag row — keeps the brand's three-color signature */}
          <div className="mt-10 flex flex-wrap items-center justify-center sm:justify-start gap-x-7 gap-y-3 font-mono text-[10px] tracking-[0.25em] uppercase text-white/70" data-testid="hero-tag-row">
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-[#1B4332]" /> Triage &amp; Stabilization
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-[#D4AF37]" /> Residential Sanctuary
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-[#0F766E]" /> Career &amp; Independence
            </span>
          </div>
        </div>
      </div>

      {/* ╔═══ Stats strip docked to the bottom of the hero ═══╗
          A translucent dark glass band carrying the three headline
          metrics. Spans the full hero width, top border in champagne
          gold to echo the brand. */}
      <div
        className="relative z-10 w-full bg-[#1E6F70]/90 backdrop-blur-md border-t border-[#D4AF37]/60"
        data-testid="hero-stats-strip"
      >
        <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto py-6 lg:py-8">
          <div className="flex flex-col md:flex-row items-stretch divide-y md:divide-y-0 md:divide-x divide-[#D4AF37]/40">
            {HERO_METRICS.map((m) => (
              <div
                key={m.testid}
                data-testid={m.testid}
                className="flex-1 py-4 md:py-1 md:px-6 lg:px-10 flex flex-col items-start justify-center"
              >
                <div className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-none">
                  <span className="tea-metal tea-metal-gold">{m.value}</span>
                </div>
                <div className="mt-2 font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#F4D88A]/85 font-semibold">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
